import { createContext, useContext, useEffect, useState } from 'react';
import {
  onAuthStateChanged,
  signInWithPopup,
  signInWithCredential,
  GoogleAuthProvider,
  signOut as firebaseSignOut,
} from 'firebase/auth';
import { auth } from './firebaseConfig';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined = loading
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => setUser(u ?? null));
    return unsub;
  }, []);

  useEffect(() => {
    const handleOnline  = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online',  handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online',  handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Popup OAuth doesn't work inside the Android WebView (see android/.../MainActivity.kt),
  // so the wrapper does native Google Sign-In via Credential Manager and hands the raw
  // ID token back through this global; we exchange it for a Firebase credential here so
  // the JS SDK (the one Firestore/Storage/IDB actually use) owns the resulting session.
  async function signInWithGoogle() {
    if (window.AndroidBridge?.signIn) {
      return new Promise((resolve, reject) => {
        window.onAndroidGoogleIdToken = (idToken) => {
          signInWithCredential(auth, GoogleAuthProvider.credential(idToken)).then(resolve, reject);
        };
        window.onAndroidGoogleSignInError = () => reject(new Error('Android sign-in cancelled or failed'));
        window.AndroidBridge.signIn();
      });
    }
    const provider = new GoogleAuthProvider();
    await signInWithPopup(auth, provider);
  }

  async function signOut() {
    await firebaseSignOut(auth);
    window.AndroidBridge?.signOut?.();
  }

  return (
    <AuthContext.Provider value={{ user, isOnline, signInWithGoogle, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
